import type { ViewContext } from '@shkola/api';
import type { GradeValue } from '@shkola/core';
import type { TFunction } from 'i18next';

/** Short form for journal cells and chips: «10», «Д», «ПП», «зар.». */
export function gradeShort(
  t: TFunction,
  value: GradeValue | null,
  special?: string | null,
): string {
  if (special === 'not_assessed' || special === 'exempt') return t(`journal.special.${special}`);
  if (!value) return '';
  switch (value.scale) {
    case 'points12':
      return String(value.points);
    case 'levels4':
      return value.level;
    case 'verbal':
      return t(`grading.verbalShort.${value.verbal}`);
    case 'pass_fail':
      return value.passed ? t('grading.passed') : t('grading.failed');
  }
}

/** Full wording for tooltips: «має значні успіхи», «достатній». */
export function gradeLong(t: TFunction, value: GradeValue | null): string {
  if (!value) return '';
  switch (value.scale) {
    case 'points12':
      return String(value.points);
    case 'levels4':
      return t(`grading.levels.${value.level}`);
    case 'verbal':
      return t(`grading.verbal.${value.verbal}`);
    case 'pass_fail':
      return value.passed ? t('grading.passed') : t('grading.failed');
  }
}

/** Colour of a grade chip: low / middle / high. */
export function gradeTone(value: GradeValue | null): 'low' | 'mid' | 'high' | 'none' {
  if (!value) return 'none';
  const ordinal = (() => {
    switch (value.scale) {
      case 'points12':
        return value.points <= 3 ? 1 : value.points <= 6 ? 2 : value.points <= 9 ? 3 : 4;
      case 'levels4':
        return ['П', 'С', 'Д', 'В'].indexOf(value.level) + 1;
      case 'verbal':
        return (
          ['needs_help', 'with_teacher_help', 'notable_progress', 'significant_success'].indexOf(
            value.verbal,
          ) + 1
        );
      case 'pass_fail':
        return value.passed ? 4 : 1;
    }
  })();
  return ordinal <= 1 ? 'low' : ordinal <= 2 ? 'mid' : 'high';
}

export const gradeToneClass = {
  low: 'bg-red-50 text-red-700 border-red-200',
  mid: 'bg-amber-50 text-amber-800 border-amber-200',
  high: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  none: 'bg-surface-muted text-text-muted border-border',
} as const;

export function contextLabel(t: TFunction, c: ViewContext): string {
  switch (c.kind) {
    case 'guardian':
      return t('context.guardian', { name: c.child.firstName, class: c.child.class?.label ?? '' });
    case 'student':
      return t('context.student', { class: c.child.class?.label ?? '' });
    case 'teacher':
      return c.homeroomClass
        ? `${t('context.teacher')} · ${t('context.homeroom', { class: c.homeroomClass.label })}`
        : t('context.teacher');
    case 'admin':
      return t('context.admin');
    case 'psychologist':
      return t('context.psychologist');
  }
}
